// src/App.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import DoorConfigurator from './components/DoorConfigurator';
import SummaryContainer from './components/SummaryContainer';
import Reviews from "./components/Reviews";
import ContactForm from './components/ContactForm';
import SaveEstimateModal from './components/SaveEstimateModal';
import ActionBar from './components/ActionBar'; // Import ActionBar
import { getAllUrlParams, buildUrl, calculateSquareFootage } from './utils/helpers';
import PricingService from './services/PricingService';
import { v4 as uuidv4 } from 'uuid'; // Import UUID
const reviews = [
  {
    comment: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique.",
      rating: 5,
      author: "Name Surname",
      position: "Position",
      company: "Company name",
    image: "https://images.unsplash.com/profile-1441298803695-accd94000cac?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&cs=tinysrgb&fit=crop&h=64&w=64&s=5a9dc749c43ce5bd60870b129a40902f", 
  },
  {
    comment: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique.",
    rating: 5,
    author: "Name Surname",
    position: "Position",
    company: "Company name",
    image: "https://images.unsplash.com/profile-1441298803695-accd94000cac?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&cs=tinysrgb&fit=crop&h=64&w=64&s=5a9dc749c43ce5bd60870b129a40902f", 
  },
  {
    comment: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique.",
    rating: 5,
    author: "Name Surname",
    position: "Position",
    company: "Company name",
    image: "https://images.unsplash.com/profile-1441298803695-accd94000cac?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&cs=tinysrgb&fit=crop&h=64&w=64&s=5a9dc749c43ce5bd60870b129a40902f", 
  },
];

function App() {
  const navigate = useNavigate();

  // Initialize params with unique IDs for each door
  const [params, setParams] = useState(() => {
    const initialParams = getAllUrlParams();
    return {
      ...initialParams,
      doors: initialParams.doors.map((door) => ({
        ...door,
        id: door.id || uuidv4(),
        widthIn: door.widthIn || '0',
        heightIn: door.heightIn || '0',
        slideDirection: door.slideDirection || '',
        openingDirection: door.openingDirection || '',
        pocketDoor: door.pocketDoor || '',
        screen: door.screen || '',
        interiorColor: door.interiorColor || '',
        exteriorColorSame: door.exteriorColorSame || '',
        exteriorColor: door.exteriorColor || '',
        hardwareColor: door.hardwareColor || '',
      })),
    };
  });

  const [showModal, setShowModal] = useState(false);
  const [totalBaseCost, setTotalBaseCost] = useState(0);
  const [totalSquareFootage, setTotalSquareFootage] = useState(0); // Added for square footage

  // Separate state for door costs
  const [doorCosts, setDoorCosts] = useState([]);

  useEffect(() => {
    const updateDoorCosts = async () => {
      const costs = await Promise.all(
        params.doors.map(async (door) => {
          if (!door.doorType) {
            // If doorType is not selected, skip calculation
            return {
              id: door.id,
              retailCost: 0,
              savings: 0,
              totalCost: 0,
              squareFootage: 0,
            };
          }

          const squareFootage = calculateSquareFootage(door);
          const costData = await PricingService.calculateCost(
            squareFootage,
            door.doorType,
            door.screen === ''
          );
          return {
            id: door.id,
            retailCost: parseFloat(costData.retailCost),
            savings: parseFloat(costData.savings),
            totalCost: parseFloat(costData.totalCost),
            squareFootage: parseFloat(squareFootage),
          };
        })
      );

      setDoorCosts(costs);

      const totalCost = costs.reduce((sum, door) => sum + door.totalCost, 0);
      const totalSquareFootage = costs.reduce((sum, door) => sum + door.squareFootage, 0);

      setTotalBaseCost(totalCost);
      setTotalSquareFootage(totalSquareFootage);
    };

    updateDoorCosts();
  }, [params.doors]);

  const addDoor = () => {
    const newDoor = {
      id: uuidv4(),
      doorType: '',
      heightFt: '0',
      heightIn: '0',
      widthFt: '0',
      widthIn: '0',
      slideDirection: '',
      openingDirection: '',
      pocketDoor: '',
      screen: '',
      interiorColor: '',
      exteriorColorSame: '',
      exteriorColor: '',
      hardwareColor: '',
    };
    setParams((prev) => {
      const updatedParams = { ...prev, doors: [...prev.doors, newDoor] };
      window.history.pushState(updatedParams, '', buildUrl(updatedParams));
      return updatedParams;
    });
  };

  const removeDoor = (doorId) => {
    const doorIndex = params.doors.findIndex((door) => door.id === doorId);
    if (doorIndex === 0 && params.doors.length === 1) {
      alert('The first door cannot be removed.');
      return;
    }
    setParams((prev) => {
      const updatedDoors = prev.doors.filter((door) => door.id !== doorId);
      const updatedParams = { ...prev, doors: updatedDoors };
      window.history.pushState(updatedParams, '', buildUrl(updatedParams));
      return updatedParams;
    });
  };

  const selectDoorOption = (doorId, option, value) => {
    setParams((prev) => {
      const updatedDoors = prev.doors.map((door) => {
        if (door.id === doorId) {
          return { ...door, [option]: value };
        }
        return door;
      });
      const updatedParams = { ...prev, doors: updatedDoors };
      window.history.pushState(updatedParams, '', buildUrl(updatedParams));
      return updatedParams;
    });
  };

  const openModal = () => {
    if (!validateDoorTypes()) return;
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const validateDoorTypes = () => {
    const invalidDoors = params.doors.filter(
      (door) =>
        !door.doorType ||
        door.doorType === '' ||
        (Number(door.widthFt || 0) + Number(door.widthIn || 0) === 0) ||
        (Number(door.heightFt || 0) + Number(door.heightIn || 0) === 0)
    );
    if (invalidDoors.length > 0) {
      alert('Please select a door type, height, and width for all doors.');
      return false;
    }
    return true;
  };

  const handleSaveEstimate = async (formData) => {
    console.log('Saving Estimate:', {
      params,
      formData,
      totalCost: totalBaseCost,
      totalSquareFootage,
    });
    // Implement actual save logic if needed
  };

  const hasInvalidDoors =
    params.doors.some(
      (door) =>
        !door.doorType ||
        door.doorType === '' ||
        (Number(door.widthFt || 0) + Number(door.widthIn || 0) === 0) ||
        (Number(door.heightFt || 0) + Number(door.heightIn || 0) === 0)
    ) || params.doors.length === 0;

  const navigateToQuoteBuilder = () => {
    if (hasInvalidDoors) {
      validateDoorTypes();
    } else {
      navigate({
        pathname: '/quote-builder',
        search: buildUrl(params),
      });
    }
  };

  return (
    <div>
      <Navbar />
      
      <main className="bg-[#F5F1ED] pb-[40px]"> {/* Padding adjusted */}
        <div className='bg-white my-0 mb-[40px]'>
          <div className="px-[20px] md:px-[80px] py-[40px] max-w-[1440px] mx-auto ">
            <div className="max-w-3xl ">
              <h1 className="text-[40px] font-semibold text-[#1D1D1D] mb-[24px] sm:block md:flex gap-x-2">Estimate <br/><span className='text-[#699357]'>Calculator</span></h1>
              <p className="text-[#626262] p-0">
                {/* Select from either Bi-Fold or Multi-Slide to start your estimate */}
                Use this tool to get a budgetary cost for your project. Select from either Bi-Fold or Multi-Slide to start your estimate.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] px-[20px] md:px-[80px] mx-auto  main-container flex flex-col lg:flex-row gap-6"> {/* Changed to lg:flex-row */}
          <div className="flex-2  p-0 mb-10 rounded">
            {params.doors.map((door) => (
              <DoorConfigurator
                key={door.id}
                door={door}
                doorId={door.id}
                selectDoorOption={selectDoorOption}
                removeDoor={removeDoor}
                params={params}
                isQuoteBuilder={false}
                hasAttemptedNext={false}
              />
            ))}
            
            <button
              onClick={addDoor}
              disabled={hasInvalidDoors}
              className={`text-gray-800 flex items-center justify-start md:justify-center font-faktum gap-x-4 w-full text-2xl font-medium leading-normal capitalize px-[16px] py-[32px] pr-[100px] md:pr-[16px] bg-white rounded-[10px] text-left md:text-center hover:bg-gray-50 mt-4 focus:outline-none focus:ring-2 focus:ring-[#699357]
              ${hasInvalidDoors ? 'opacity-50 cursor-not-allowed' : ''
										} transition-colors`}
              
								>
									<span className='block'>+</span> Want to Add Another Door?
								</button>
          </div>


          <SummaryContainer
            params={params}
            doorCosts={doorCosts} // Pass doorCosts
            removeDoor={removeDoor}
            validateDoorTypes={validateDoorTypes}
            openModal={openModal}
            context="estimate"
            navigateToQuoteBuilder={navigateToQuoteBuilder}
          />
        </div>
      </main>

      {/* Conditionally Render ActionBar */}
      {!hasInvalidDoors && totalBaseCost > 0 && (
        <ActionBar
          totalBaseCost={totalBaseCost}
          buttons={[
            
            {
              label: 'Build Your Quote',
              onClick: navigateToQuoteBuilder,
              primary: true,
              svg: 'up-right arrow',
              disabled: hasInvalidDoors,
              ariaLabel: hasInvalidDoors
                ? 'Please select a door type, height, and width for all doors before building a quote.'
                : 'Build Quote',
              title: hasInvalidDoors
                ? 'Please select a door type, height, and width for all doors before building a quote.'
                : 'Build Quote',
            },
            {
              label: 'Save Estimate',
              onClick: openModal,
              primary: false, // Secondary styling
              disabled: false,
              ariaLabel: 'Save Estimate',
              title: 'Save Estimate',
              customClass: 'underline', // Removed 'underline' and set 'no-underline'
            },
          ]}
        />
      )}
      {/* <Reviews reviews={reviews} /> */}
      <ContactForm
      subheading="Have a Question?"
      heading="Contact Us"
      paragraph=" Customize your bi-fold glass doors by selecting size, door type, color, and more. Our easy-to-use calculator gives you an instant estimate, so you know what to expect before you buy. SSjfajshdfiojfaohndofv  aidfj if asoid faossdjf oiaiosf idoaj oida o."
      label="Contact Us"
      link="#"
      ></ContactForm>
      <SaveEstimateModal
        isOpen={showModal}
        onClose={closeModal}
        doors={params.doors}
        doorCosts={doorCosts}
        totalCost={totalBaseCost}
        totalSquareFootage={totalSquareFootage}
      />
    </div>
  );
}

export default App;
